<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useCountriesStore } from "~/stores/countries";
import { type Content, DEFAULT_CONTENT, useContentStore } from "./stores/content";
import type {
  AppLink,
  OfficeContact,
  SocialNetwork
} from "./types/content";
import { IScrollSliderItem } from "./components/slider/ScrollSlider.vue";
import { Env } from "@/types/entities";

const { saveContent } = useContentStore();
const { currentCountry } = storeToRefs(useCountriesStore());

const isProd = process.env.NODE_ENV === Env.production;

useSeoMeta({
  robots: isProd ? "index, follow" : "noindex, nofollow"
});

const { brand } = useRuntimeConfig().public;
const { data } = await useAsyncData("content", async () => {
  const countryCode = currentCountry.value.code;
  const [feedback, officeContactData, videoLinkData] =
    await Promise.all([
      queryCollection("content").path(
        `/sections/${brand}/${countryCode}/parters-logo-section`
      ).first(),
      queryCollection("content").path(
        `/sections/${brand}/${countryCode}/contacts-section`
      ).first(),
      queryCollection("content").path(
        `/sections/${brand}/${countryCode}/video-section`
      ).first(),
    ]);

  const content: Content = {
    companies: (feedback?.meta?.companies as IScrollSliderItem[]) ?? DEFAULT_CONTENT.companies,
    officeContact:
      (officeContactData?.meta?.officeContact as OfficeContact) ??
      DEFAULT_CONTENT.officeContact,
    appLinks: (officeContactData?.meta?.appLinks as AppLink[]) ?? DEFAULT_CONTENT.appLinks,
    socialNetworks:
      (officeContactData?.meta?.socialNetworks as SocialNetwork[]) ?? DEFAULT_CONTENT.socialNetworks,
    videoLink: (videoLinkData?.meta?.videoLink as string) ?? DEFAULT_CONTENT.videoLink,
  };

  return content;
}, {
  default: () => DEFAULT_CONTENT,
  watch: [() => currentCountry.value.code],
});

watch(data, (newData) => {
  if (newData) {
    saveContent(newData);
  }
}, { immediate: true });
</script>

<template>
  <NuxtLayout>
    <NuxtPage :content="data ?? DEFAULT_CONTENT" />
  </NuxtLayout>
</template>
